import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Edit, Trash2, Euro, Calendar, Star, Shield, MessageCircle, CalendarX, Flame, Percent, AlertTriangle, PauseCircle, PlayCircle, FileText, Download, ScanLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ChatDialog from "@/components/ChatDialog";
import OwnerQrScanner from "@/components/OwnerQrScanner";

export default function OwnerDashboard() {
  const { t, user, loading, logout } = useApp();
  const nav = useNavigate();
  const [pools, setPools] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [threads, setThreads] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [chat, setChat] = useState(null); // {poolId, guestId, title}
  const [promoPool, setPromoPool] = useState(null);
  const [promoForm, setPromoForm] = useState({ discount_pct: 0.1, promo_active: true, promo_label: "", full_day_discount_price: "", half_day_discount_price: "", daily_offer: "" });
  const [blockPool, setBlockPool] = useState(null);
  const [blockRange, setBlockRange] = useState({ start_date: "", end_date: "" });
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [suspension, setSuspension] = useState({ suspended: false, suspended_at: null, auto_delete_at: null, grace_months: 8 });
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [busySuspend, setBusySuspend] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [profile, setProfile] = useState({});
  const [profileForm, setProfileForm] = useState({ vat_id: "", fiscal_code: "", sdi_code: "", pec_email: "", business_name: "", business_address: "", business_city: "", business_zip: "", business_province: "", business_country: "IT" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualCheckinId, setManualCheckinId] = useState("");
  const [busyManual, setBusyManual] = useState(false);

  const hotelName = pools[0]?.hotel_name || user?.name || "";
  const deleteMyAccount = async () => {
    if (deleteAccountConfirm.trim() !== hotelName) {
      toast.error("Il nome non corrisponde");
      return;
    }
    setDeletingAccount(true);
    try {
      const { data: resp } = await api.delete("/owner/account");
      toast.success(`Account e ${resp.pools_removed} piscine eliminati`);
      setShowDeleteAccount(false);
      logout();
      nav("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione fallita");
      setDeletingAccount(false);
    }
  };

  const suspendHotel = async () => {
    setBusySuspend(true);
    try {
      const { data: resp } = await api.post("/owner/suspend");
      const refundNote = resp.future_bookings_to_refund > 0
        ? ` · ${resp.future_bookings_to_refund} prenotazioni future annullate`
        : "";
      toast.success(`Hotel sospeso · ${resp.pools_hidden} piscine nascoste${refundNote}`);
      setShowSuspendDialog(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sospensione fallita");
    } finally {
      setBusySuspend(false);
    }
  };

  const reactivateHotel = async () => {
    setBusySuspend(true);
    try {
      const { data: resp } = await api.post("/owner/reactivate");
      toast.success(`Hotel riattivato · ${resp.pools_restored} piscine visibili`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Riattivazione fallita");
    } finally {
      setBusySuspend(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    if (user.role !== "owner") { nav("/guest"); return; }
    reload();
    // eslint-disable-next-line
  }, [user, loading, nav]);

  const reload = () => Promise.all([api.get("/pools/mine"), api.get("/bookings/mine"), api.get("/messages/threads"), api.get("/owner/status"), api.get("/owner/invoices"), api.get("/owner/profile")])
    .then(([p, b, tk, st, inv, prof]) => {
      setPools(p.data); setBookings(b.data); setThreads(tk.data); setSuspension(st.data); setInvoices(inv.data.invoices || []);
      setProfile(prof.data);
      setProfileForm({
        vat_id: prof.data.vat_id || "", fiscal_code: prof.data.fiscal_code || "",
        sdi_code: prof.data.sdi_code || "", pec_email: prof.data.pec_email || "",
        business_name: prof.data.business_name || "", business_address: prof.data.business_address || "",
        business_city: prof.data.business_city || "", business_zip: prof.data.business_zip || "",
        business_province: prof.data.business_province || "", business_country: prof.data.business_country || "IT",
      });
    });

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/owner/profile", profileForm);
      setProfile(data);
      toast.success("Dati fiscali salvati — saranno usati nelle prossime fatture");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSavingProfile(false);
    }
  };

  const downloadInvoiceXml = async (invoiceId, number) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owner/invoices/${invoiceId}/xml`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `SDI_${number}.xml`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download XML fallito"); }
  };

  const del = async (id) => { if (!window.confirm("Delete this pool?")) return; await api.delete(`/pools/${id}`); toast.success("Deleted"); reload(); };

  const submitGuestReview = async () => {
    try {
      await api.post("/host-reviews", { booking_id: reviewing.booking_id, rating, comment });
      toast.success(t("reviewForm.success"));
      setReviewing(null); setComment(""); setRating(5);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openPromo = (pool) => {
    setPromoPool(pool);
    setPromoForm({
      discount_pct: pool.discount_pct || 0.1,
      promo_active: pool.promo_active || false,
      promo_label: pool.promo_label || "",
      full_day_discount_price: pool.full_day_discount_price || "",
      half_day_discount_price: pool.half_day_discount_price || "",
      daily_offer: pool.daily_offer || "",
    });
  };
  const savePromo = async () => {
    try {
      await api.patch(`/pools/${promoPool.pool_id}/promo`, {
        discount_pct: +promoForm.discount_pct,
        promo_active: promoForm.promo_active,
        promo_label: promoForm.promo_label,
      });
      await api.patch(`/pools/${promoPool.pool_id}`, {
        full_day_discount_price: promoForm.full_day_discount_price === "" ? 0 : +promoForm.full_day_discount_price,
        half_day_discount_price: promoForm.half_day_discount_price === "" ? 0 : +promoForm.half_day_discount_price,
        daily_offer: promoForm.daily_offer || "",
      });
      toast.success(t("promo.saved"));
      setPromoPool(null); reload();
    } catch (e) { toast.error("Failed"); }
  };

  const doBlock = async (mode) => {
    if (!blockRange.start_date || !blockRange.end_date) return;
    try {
      const path = mode === "block" ? "block-range" : "unblock-range";
      const r = await api.post(`/pools/${blockPool.pool_id}/${path}`, blockRange);
      toast.success(`${t("block.success")}: ${r.data.blocked || r.data.unblocked} days`);
      setBlockPool(null); setBlockRange({ start_date: "", end_date: "" }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadExport = async (fmt) => {
    try {
      const token = localStorage.getItem("token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/bookings/export?fmt=${fmt}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `poolpass_bookings.${fmt}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Export ${fmt.toUpperCase()} ${t("owner.exportDone")}`);
    } catch (e) { toast.error("Export failed"); }
  };

  const earnings = bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + (b.subtotal || 0), 0);
  const paidBookings = bookings.filter((b) => b.payment_status === "paid");
  const toReview = paidBookings.filter((b) => !b.host_reviewed);

  const todayISO = new Date().toISOString().slice(0, 10);
  const todayBookings = paidBookings.filter((b) => b.date === todayISO);

  const markCheckedIn = (bid) => {
    setBookings((prev) => prev.map((b) => b.booking_id === bid ? { ...b, checked_in: true, checked_in_at: new Date().toISOString() } : b));
  };

  if (!user) return null;
  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Suspension banner */}
      {suspension.suspended && (
        <div className="mb-8 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 flex items-start gap-4" data-testid="suspension-banner">
          <div className="w-11 h-11 rounded-xl bg-amber-200 flex items-center justify-center flex-shrink-0">
            <PauseCircle className="w-6 h-6 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-semibold text-amber-900">Hotel in sospensione</div>
            <div className="text-sm text-amber-800 mt-1">
              Le tue piscine <strong>non sono visibili</strong> ai guest. Sospeso il <strong>{formatDate(suspension.suspended_at)}</strong>.
            </div>
            <div className="text-sm text-red-700 font-semibold mt-2 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> Eliminazione automatica il {formatDate(suspension.auto_delete_at)} se non riattivi
            </div>
          </div>
          <Button
            onClick={reactivateHotel}
            disabled={busySuspend}
            className="rounded-full bg-green-600 hover:bg-green-700 flex-shrink-0"
            data-testid="reactivate-btn"
          >
            <PlayCircle className="w-4 h-4 mr-2" /> {busySuspend ? "…" : "Riattiva ora"}
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline text-[#E27B58] mb-2">{t("owner.badge")}</div>
          <h1 className="font-display text-4xl font-bold">{t("owner.title")}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setScannerOpen(true)}
            className="rounded-full bg-[#E27B58] hover:bg-[#C56A4A]"
            data-testid="scan-qr-btn"
          >
            <ScanLine className="w-4 h-4 mr-1" /> Scansiona QR
          </Button>
          <Link to="/owner/new"><Button className="rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid="new-pool-btn"><Plus className="w-4 h-4 mr-1" /> {t("owner.new")}</Button></Link>
        </div>
      </div>

      {/* Convalida manuale ID transazione */}
      <form
        className="mb-8 bg-white border border-border rounded-2xl p-5 flex flex-wrap items-end gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          const id = manualCheckinId.trim().toUpperCase();
          if (!id) return;
          setBusyManual(true);
          try {
            const { data } = await api.post("/bookings/checkin", { short_id: id });
            markCheckedIn(data.booking_id);
            if (data.already_checked_in) {
              toast.info(`Già entrato: ${data.guest_name || data.booking_id}`);
            } else {
              toast.success(`Prenotazione Valida. Incassare €${data.amount.toFixed(2)} tramite ${data.payment_method_label} da ${data.guest_name || "cliente"}`);
            }
            setManualCheckinId("");
          } catch (err) {
            toast.error(err.response?.data?.detail || "ID non valido");
          } finally { setBusyManual(false); }
        }}
        data-testid="manual-checkin-form"
      >
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Convalida manuale ingresso</div>
          <Input
            placeholder="Inserisci ID transazione (es. PP-A3F7QX)"
            value={manualCheckinId}
            onChange={(e) => setManualCheckinId(e.target.value)}
            className="font-mono uppercase text-base tracking-wider"
            style={{ letterSpacing: "0.1em" }}
            data-testid="manual-checkin-input"
          />
        </div>
        <Button type="submit" disabled={busyManual || !manualCheckinId.trim()} className="rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid="manual-checkin-submit">
          {busyManual ? "..." : "Conferma"}
        </Button>
      </form>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><Euro className="w-4 h-4" /> Incassato in loco (totale)</div>
          <div className="font-display text-3xl font-bold">€{earnings.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground mt-1">Somma delle prenotazioni pagate a struttura</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><Calendar className="w-4 h-4" /> {t("owner.bookings")}</div>
          <div className="font-display text-3xl font-bold">{paidBookings.length}</div>
          <div className="text-xs text-muted-foreground mt-1">{t("owner.confirmed")}</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">{t("admin.pools")}</div>
          <div className="font-display text-3xl font-bold">{pools.length}</div>
          <div className="text-xs text-muted-foreground mt-1">{t("owner.published")}</div>
        </div>
      </div>

      {/* Ingressi di oggi — lista giornaliera con stato check-in */}
      {todayBookings.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-3">
            <ScanLine className="w-5 h-5 text-[#E27B58]" />
            <h2 className="font-display text-2xl font-bold">Ingressi di oggi</h2>
            <span className="text-sm text-muted-foreground">
              · {todayBookings.filter((b) => b.checked_in).length}/{todayBookings.length} entrati
            </span>
          </div>
          <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="today-bookings">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Stato</th>
                  <th className="text-left px-4 py-3 font-semibold">Ospite</th>
                  <th className="text-left px-4 py-3 font-semibold">Slot</th>
                  <th className="text-right px-4 py-3 font-semibold">Metodo</th>
                  <th className="text-right px-4 py-3 font-semibold">Importo</th>
                </tr>
              </thead>
              <tbody>
                {todayBookings.map((b) => {
                  const done = !!b.checked_in;
                  const pm = b.payment_method === "card_on_site" ? "Carta" : "Contanti";
                  return (
                    <tr
                      key={b.booking_id}
                      className={`border-t border-border ${done ? "bg-green-50/40 text-muted-foreground" : ""}`}
                      data-testid={`today-row-${b.booking_id}`}
                    >
                      <td className="px-4 py-3">
                        {done ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-100 px-2 py-1 rounded-full" data-testid={`checkedin-badge-${b.booking_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Entrato
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                            In attesa
                          </span>
                        )}
                      </td>
                      <td className={`px-4 py-3 ${done ? "line-through" : ""}`}>{b.guest_name || b.guest_id?.slice(-8)}</td>
                      <td className={`px-4 py-3 ${done ? "line-through" : ""}`}>{b.slot === "half" ? t("pool.slotHalf") : t("pool.slotFull")}</td>
                      <td className={`px-4 py-3 text-right ${done ? "line-through" : ""}`}>{pm}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${done ? "line-through" : "text-[#005C7A]"}`}>€{(b.amount || 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Guests to review */}
      {toReview.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-2"><Shield className="w-5 h-5 text-[#005C7A]" /><h2 className="font-display text-2xl font-bold">{t("owner.toReview")}</h2></div>
          <p className="text-sm text-muted-foreground mb-4">{t("owner.reviewGate")}</p>
          <div className="grid md:grid-cols-2 gap-4">
            {toReview.map((b) => (
              <div key={b.booking_id} className="bg-white border border-border rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={`toreview-${b.booking_id}`}>
                <div>
                  <div className="font-semibold">{b.pool_title}</div>
                  <div className="text-sm text-muted-foreground">{b.date} · {b.guests} guest(s)</div>
                </div>
                <Button size="sm" onClick={() => { setReviewing(b); setRating(5); setComment(""); }} className="rounded-full bg-[#E27B58] hover:bg-[#C56A4A]" data-testid={`review-guest-${b.booking_id}`}>
                  <Star className="w-3.5 h-3.5 mr-1" /> {t("owner.reviewGuest")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      {threads.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4"><MessageCircle className="w-5 h-5 text-[#005C7A]" /><h2 className="font-display text-2xl font-bold">{t("chat.threads")}</h2></div>
          <div className="grid md:grid-cols-2 gap-4">
            {threads.map((th) => (
              <button key={`${th.pool_id}-${th.guest_id}`} onClick={() => setChat({ poolId: th.pool_id, guestId: th.guest_id, title: `${th.other_name} · ${th.pool_title}` })} className="bg-white border border-border rounded-2xl p-4 flex items-center gap-3 text-left hover:border-[#005C7A]/40 transition" data-testid={`thread-${th.pool_id}`}>
                <div className="w-12 h-12 rounded-full bg-muted overflow-hidden shrink-0">
                  {th.cover ? <img src={fileUrl(th.cover)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#005C7A]/10" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{th.other_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{th.pool_title}</div>
                  <div className="text-sm text-muted-foreground truncate mt-0.5">{th.last_from_me && "→ "}{th.last_message}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {pools.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-2xl">{t("owner.noPools")}</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {pools.map((p) => (
            <div key={p.pool_id} className="bg-white border border-border rounded-2xl overflow-hidden" data-testid={`owner-pool-${p.pool_id}`}>
              <div className="aspect-video bg-muted overflow-hidden relative">
                <img src={p.photos?.[0] ? fileUrl(p.photos[0]) : "https://images.unsplash.com/photo-1582533568805-78a15dcb01b5?w=600"} alt="" className="w-full h-full object-cover" />
                {p.promo_active && p.discount_pct > 0 && (
                  <div className="absolute top-2 left-2 bg-[#E27B58] text-white text-xs font-bold px-2 py-1 rounded-full">−{Math.round(p.discount_pct * 100)}%</div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg font-semibold mb-1">{p.title}</h3>
                <div className="text-sm text-muted-foreground mb-3">{p.city} · €{p.price_per_day}/pers</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Link to={`/owner/edit/${p.pool_id}`}><Button variant="outline" size="sm" className="w-full rounded-full" data-testid={`edit-${p.pool_id}`}><Edit className="w-3.5 h-3.5 mr-1" /> {t("owner.edit")}</Button></Link>
                  <Link to={`/owner/availability/${p.pool_id}`}><Button variant="outline" size="sm" className="w-full rounded-full" data-testid={`avail-${p.pool_id}`}><Calendar className="w-3.5 h-3.5 mr-1" /> Slots</Button></Link>
                  <Button variant="outline" size="sm" onClick={() => openPromo(p)} className="w-full rounded-full" data-testid={`promo-${p.pool_id}`}><Percent className="w-3.5 h-3.5 mr-1" /> Promo</Button>
                  <Button variant="outline" size="sm" onClick={() => { setBlockPool(p); setBlockRange({ start_date: "", end_date: "" }); }} className="w-full rounded-full" data-testid={`block-${p.pool_id}`}><CalendarX className="w-3.5 h-3.5 mr-1" /> Block</Button>
                </div>
                <Button variant="outline" size="sm" onClick={() => del(p.pool_id)} className="w-full rounded-full text-destructive" data-testid={`delete-${p.pool_id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> {t("owner.delete")}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {paidBookings.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display text-2xl font-bold">{t("owner.recent")}</h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadExport("csv")} className="rounded-full" data-testid="export-csv"><Euro className="w-3.5 h-3.5 mr-1" /> CSV</Button>
              <Button size="sm" variant="outline" onClick={() => downloadExport("xlsx")} className="rounded-full" data-testid="export-xlsx"><Euro className="w-3.5 h-3.5 mr-1" /> Excel</Button>
            </div>
          </div>
          <div className="bg-white border border-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr>
                <th className="text-left px-4 py-3 font-semibold">Stato</th>
                <th className="text-left px-4 py-3 font-semibold">{t("owner.date")}</th>
                <th className="text-left px-4 py-3 font-semibold">{t("owner.customer")}</th>
                <th className="text-left px-4 py-3 font-semibold">{t("owner.access")}</th>
                <th className="text-right px-4 py-3 font-semibold">Metodo</th>
                <th className="text-right px-4 py-3 font-semibold">Importo (in loco)</th>
              </tr></thead>
              <tbody>
                {paidBookings.map((b) => {
                  const total = b.amount || 0;
                  const done = !!b.checked_in;
                  const pm = b.payment_method === "card_on_site" ? "Carta" : b.payment_method === "cash" ? "Contanti" : "—";
                  return (
                    <tr key={b.booking_id} className={`border-t border-border ${done ? "bg-green-50/30" : ""}`}>
                      <td className="px-4 py-3">
                        {done ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                            <CheckCircle2 className="w-3 h-3" /> Entrato
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 ${done ? "line-through text-muted-foreground" : ""}`}>{b.date}</td>
                      <td className={`px-4 py-3 ${done ? "line-through text-muted-foreground" : ""}`}>{b.guest_name || b.guest_id?.slice(-8)}</td>
                      <td className={`px-4 py-3 ${done ? "line-through text-muted-foreground" : ""}`}>{b.slot === "half" ? t("pool.slotHalf") : t("pool.slotFull")}</td>
                      <td className={`px-4 py-3 text-right ${done ? "line-through text-muted-foreground" : ""}`}>{pm}</td>
                      <td className={`px-4 py-3 text-right font-bold ${done ? "line-through text-muted-foreground" : "text-[#005C7A]"}`}>€{total.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Host review dialog */}
      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("reviewForm.guestTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("reviewForm.guestPrompt")}</p>
          <div className="flex gap-1 justify-center py-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} data-testid={`hr-star-${n}`}>
                <Star className={`w-8 h-8 ${n <= rating ? "fill-[#E27B58] text-[#E27B58]" : "text-muted-foreground/30"}`} />
              </button>
            ))}
          </div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Your comment..." data-testid="hr-comment" />
          <Button onClick={submitGuestReview} className="rounded-full bg-[#005C7A]" data-testid="submit-hr">{t("reviewForm.submit")}</Button>
        </DialogContent>
      </Dialog>

      {/* Promo dialog */}
      <Dialog open={!!promoPool} onOpenChange={(o) => !o && setPromoPool(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Flame className="w-5 h-5 text-[#E27B58]" /> {promoPool?.title}</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground">{t("pricing.helper")}</div>
          <div>
            <Label>{t("pricing.dailyOffer")}</Label>
            <Input value={promoForm.daily_offer} onChange={(e) => setPromoForm({ ...promoForm, daily_offer: e.target.value })} placeholder={t("pricing.dailyOfferPlaceholder")} data-testid="daily-offer" maxLength={60} />
            {promoForm.daily_offer && (
              <div className="mt-2 inline-flex items-center gap-1.5 bg-green-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-lg">
                ✨ {promoForm.daily_offer}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 border-t border-border pt-4">
            <div>
              <Label className="text-xs">{t("pricing.fullBase")}</Label>
              <div className="p-2 bg-muted/40 rounded text-sm font-semibold">€{promoPool?.price_per_day}</div>
            </div>
            <div>
              <Label className="text-xs">{t("pricing.halfBase")}</Label>
              <div className="p-2 bg-muted/40 rounded text-sm font-semibold">€{promoPool ? (promoPool.price_per_day * (promoPool.half_day_price_pct ?? 0.6)).toFixed(2) : ""}</div>
            </div>
            <div>
              <Label className="text-xs">{t("pricing.fullDisc")}</Label>
              <Input type="number" min="0" step="0.5" value={promoForm.full_day_discount_price} onChange={(e) => setPromoForm({ ...promoForm, full_day_discount_price: e.target.value })} placeholder="0" data-testid="full-disc" />
            </div>
            <div>
              <Label className="text-xs">{t("pricing.halfDisc")}</Label>
              <Input type="number" min="0" step="0.5" value={promoForm.half_day_discount_price} onChange={(e) => setPromoForm({ ...promoForm, half_day_discount_price: e.target.value })} placeholder="0" data-testid="half-disc" />
            </div>
          </div>
          <div className="text-xs text-muted-foreground">{t("pricing.disableHint")}</div>
          <div className="flex items-center justify-between p-3 border border-border rounded-lg">
            <div>
              <div className="font-semibold text-sm">{t("promo.enable")} (%)</div>
              <div className="text-xs text-muted-foreground">Sconto % globale (usato se non imposti prezzi scontati sopra)</div>
            </div>
            <Switch checked={promoForm.promo_active} onCheckedChange={(v) => setPromoForm({ ...promoForm, promo_active: v })} data-testid="promo-switch" />
          </div>
          {promoForm.promo_active && (
            <div>
              <Label>{t("promo.discount")}</Label>
              <div className="flex items-center gap-3">
                <input type="range" min="0" max="0.5" step="0.05" value={promoForm.discount_pct} onChange={(e) => setPromoForm({ ...promoForm, discount_pct: +e.target.value })} className="flex-1" data-testid="promo-range" />
                <span className="w-14 text-right font-semibold">{Math.round(promoForm.discount_pct * 100)}%</span>
              </div>
            </div>
          )}
          <Button onClick={savePromo} className="rounded-full bg-[#005C7A]" data-testid="promo-save">{t("pricing.save")}</Button>
        </DialogContent>
      </Dialog>

      {/* Block range dialog */}
      <Dialog open={!!blockPool} onOpenChange={(o) => !o && setBlockPool(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><CalendarX className="w-5 h-5 text-[#005C7A]" /> {t("block.title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("block.helper")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("block.start")}</Label><Input type="date" value={blockRange.start_date} onChange={(e) => setBlockRange({ ...blockRange, start_date: e.target.value })} data-testid="block-start" /></div>
            <div><Label>{t("block.end")}</Label><Input type="date" value={blockRange.end_date} onChange={(e) => setBlockRange({ ...blockRange, end_date: e.target.value })} data-testid="block-end" /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => doBlock("block")} className="rounded-full bg-[#005C7A] flex-1" data-testid="do-block">{t("block.blockBtn")}</Button>
            <Button onClick={() => doBlock("unblock")} variant="outline" className="rounded-full flex-1" data-testid="do-unblock">{t("block.unblockBtn")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {chat && <ChatDialog open={!!chat} onOpenChange={(o) => !o && setChat(null)} poolId={chat.poolId} guestId={chat.guestId} title={chat.title} />}

      <OwnerQrScanner open={scannerOpen} onOpenChange={setScannerOpen} onCheckedIn={markCheckedIn} />

      {/* Invoices — Le mie fatture */}
      <div className="mt-12 pt-8 border-t border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#005C7A]/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-[#005C7A]" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold">Dati fiscali & fatturazione</h2>
            <p className="text-sm text-muted-foreground">Compila la P.IVA e il codice SDI: le prossime fatture saranno intestate ai tuoi dati e conformi al formato SDI italiano.</p>
          </div>
        </div>

        {/* Fiscal profile form */}
        <div className="bg-white border border-border rounded-2xl p-6 mb-8" data-testid="fiscal-profile-card">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Denominazione (Ragione sociale)</Label><Input value={profileForm.business_name} onChange={(e) => setProfileForm({ ...profileForm, business_name: e.target.value })} placeholder="Hotel Belvedere SRL" data-testid="fisc-business_name" /></div>
            <div><Label>P.IVA</Label><Input value={profileForm.vat_id} onChange={(e) => setProfileForm({ ...profileForm, vat_id: e.target.value })} placeholder="IT12345678901" data-testid="fisc-vat_id" /></div>
            <div><Label>Codice Fiscale</Label><Input value={profileForm.fiscal_code} onChange={(e) => setProfileForm({ ...profileForm, fiscal_code: e.target.value })} placeholder="Codice fiscale (16 char) o P.IVA" data-testid="fisc-fiscal_code" /></div>
            <div><Label>Codice destinatario SDI (7 char)</Label><Input value={profileForm.sdi_code} onChange={(e) => setProfileForm({ ...profileForm, sdi_code: e.target.value.toUpperCase() })} maxLength={7} placeholder="ABCDE12 · usa 0000000 se PEC" data-testid="fisc-sdi_code" /></div>
            <div className="md:col-span-2"><Label>PEC (opzionale, se non hai SDI)</Label><Input value={profileForm.pec_email} onChange={(e) => setProfileForm({ ...profileForm, pec_email: e.target.value })} placeholder="hotel@pec.it" data-testid="fisc-pec_email" /></div>
            <div className="md:col-span-2"><Label>Indirizzo sede legale</Label><Input value={profileForm.business_address} onChange={(e) => setProfileForm({ ...profileForm, business_address: e.target.value })} placeholder="Via Roma 1" data-testid="fisc-business_address" /></div>
            <div><Label>Città</Label><Input value={profileForm.business_city} onChange={(e) => setProfileForm({ ...profileForm, business_city: e.target.value })} data-testid="fisc-business_city" /></div>
            <div className="grid grid-cols-2 gap-3"><div><Label>CAP</Label><Input value={profileForm.business_zip} onChange={(e) => setProfileForm({ ...profileForm, business_zip: e.target.value })} maxLength={5} data-testid="fisc-business_zip" /></div><div><Label>Prov.</Label><Input value={profileForm.business_province} onChange={(e) => setProfileForm({ ...profileForm, business_province: e.target.value.toUpperCase() })} maxLength={2} placeholder="MI" data-testid="fisc-business_province" /></div></div>
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={saveProfile} disabled={savingProfile} className="rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid="fisc-save">
              {savingProfile ? "Salvataggio…" : "Salva dati fiscali"}
            </Button>
          </div>
          {!profile.vat_id && (
            <div className="mt-4 bg-amber-50 border-l-4 border-amber-400 rounded-r p-3 text-xs text-amber-900">
              ⚠️ Senza P.IVA le fatture riporteranno "P.IVA non fornita" e l'XML SDI non sarà consegnabile.
            </div>
          )}
        </div>

        <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Le mie fatture</h3>
        {invoices.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-border rounded-2xl text-sm text-muted-foreground" data-testid="invoices-empty">
            Nessuna fattura emessa ancora. La prima arriverà a inizio del prossimo mese se avrai avuto prenotazioni pagate.
          </div>
        ) : (
          <div className="bg-white border border-border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Numero</th>
                  <th className="text-left px-4 py-3">Periodo</th>
                  <th className="text-right px-4 py-3">Prenot.</th>
                  <th className="text-right px-4 py-3">Volume</th>
                  <th className="text-right px-4 py-3">Imponibile</th>
                  <th className="text-right px-4 py-3">IVA 22%</th>
                  <th className="text-right px-4 py-3">Totale</th>
                  <th className="text-right px-4 py-3">Download</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="border-t border-border hover:bg-muted/20" data-testid={`invoice-${inv.invoice_id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold">{inv.number}</td>
                    <td className="px-4 py-3">{inv.period_label}</td>
                    <td className="px-4 py-3 text-right">{inv.bookings_count}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">€{inv.gross_volume.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">€{inv.taxable.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">€{inv.iva.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-display font-bold text-[#005C7A]">€{inv.total.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1.5 justify-end">
                        <a
                          href="#" onClick={async (e) => {
                            e.preventDefault();
                            const token = localStorage.getItem("token");
                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owner/invoices/${inv.invoice_id}/download`, { headers: { Authorization: `Bearer ${token}` } });
                            const blob = await res.blob();
                            window.open(URL.createObjectURL(blob), "_blank");
                          }}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#005C7A] text-white hover:bg-[#004A63]"
                          data-testid={`invoice-download-${inv.invoice_id}`}
                        >
                          <Download className="w-3 h-3" /> PDF
                        </a>
                        <button
                          onClick={() => downloadInvoiceXml(inv.invoice_id, inv.number)}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white border border-[#005C7A] text-[#005C7A] hover:bg-[#005C7A]/5"
                          data-testid={`invoice-xml-${inv.invoice_id}`}
                          title="XML SDI (FatturaPA)"
                        >
                          <Download className="w-3 h-3" /> XML
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Danger zone — Suspend or Delete hotel */}
      <div className="mt-12 pt-8 border-t border-red-200">
        <div className="bg-red-50/50 border border-red-200 rounded-2xl p-6 max-w-2xl">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-red-900">Zona pericolo</h3>
              <p className="text-sm text-red-800/80 mt-0.5">Sospendi temporaneamente l'hotel per nascondere le piscine ai guest, oppure elimina l'account. La sospensione è reversibile.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {!suspension.suspended ? (
              <Button
                variant="outline"
                onClick={() => setShowSuspendDialog(true)}
                className="border-amber-400 text-amber-800 hover:bg-amber-100"
                data-testid="owner-suspend-btn"
              >
                <PauseCircle className="w-3.5 h-3.5 mr-2" /> Sospendi visibilità
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={reactivateHotel}
                disabled={busySuspend}
                className="border-green-500 text-green-700 hover:bg-green-50"
                data-testid="owner-reactivate-danger-btn"
              >
                <PlayCircle className="w-3.5 h-3.5 mr-2" /> Riattiva visibilità
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => { setShowDeleteAccount(true); setDeleteAccountConfirm(""); }}
              className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
              data-testid="owner-delete-account-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Elimina il mio hotel
            </Button>
          </div>
        </div>
      </div>

      {/* Suspend confirmation dialog */}
      <AlertDialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <AlertDialogContent data-testid="owner-suspend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <PauseCircle className="w-5 h-5" /> Sospendere la visibilità dell'hotel?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2 text-sm">
                <p>Le tue <strong>{pools.length}</strong> piscine verranno nascoste dalla mappa, dalla ricerca e dalla home. I dati (recensioni, prenotazioni passate, foto) restano intatti.</p>
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-3 text-xs text-blue-900 space-y-1">
                  <div className="font-semibold">🛡️ Protezione automatica ospiti</div>
                  <div>Se hai prenotazioni <strong>future</strong>, i guest riceveranno un'email di scuse e le loro prenotazioni verranno annullate. Nessun addebito da riparare (pagamento in loco). La tua reputazione è al sicuro.</div>
                </div>
                <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-3 text-xs text-amber-900 space-y-1">
                  <div className="font-semibold">⏳ Cancellazione automatica dopo 8 mesi</div>
                  <div>Se non riattivi entro <strong>8 mesi</strong>, l'hotel e tutti i dati saranno eliminati definitivamente.</div>
                  <div>Puoi <strong>riattivare in qualsiasi momento</strong> con un click.</div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="owner-suspend-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); suspendHotel(); }}
              disabled={busySuspend}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="owner-suspend-confirm"
            >
              {busySuspend ? "Sospensione…" : "Sospendi ora"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete account confirmation dialog */}
      <AlertDialog open={showDeleteAccount} onOpenChange={setShowDeleteAccount}>
        <AlertDialogContent data-testid="owner-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> Eliminare l'hotel definitivamente?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-sm">Stai per eliminare <strong className="text-[#1A202C]">{hotelName}</strong> e tutti i dati collegati.</p>
                <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3 text-xs text-red-900 space-y-1">
                  <div>Verranno rimossi in cascata:</div>
                  <div>· <strong>{pools.length}</strong> piscine</div>
                  <div>· <strong>{bookings.length}</strong> prenotazioni</div>
                  <div>· tutte le recensioni, foto, messaggi</div>
                  <div>· il tuo account gestore (verrai disconnesso)</div>
                </div>
                <p className="text-sm font-semibold text-red-700">Questa azione è irreversibile.</p>
                <div>
                  <Label className="text-xs">Per confermare, digita il nome del tuo hotel <strong>({hotelName})</strong>:</Label>
                  <Input
                    value={deleteAccountConfirm}
                    onChange={(e) => setDeleteAccountConfirm(e.target.value)}
                    placeholder={hotelName}
                    autoComplete="off"
                    data-testid="owner-delete-confirm-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="owner-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deleteMyAccount(); }}
              disabled={deletingAccount || deleteAccountConfirm.trim() !== hotelName}
              className="bg-red-600 hover:bg-red-700"
              data-testid="owner-delete-confirm"
            >
              {deletingAccount ? "Eliminazione…" : "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
